/**
 * SOMA Outcome Planner — main entry point.
 *
 * A planner-facing web app for managing patient outcome monitoring
 * care plans. Reads catalogue data from _observationDefinition and
 * _activityDefinition; manages per-patient _carePlan and _goal
 * documents.
 */

import type { PatientInfo } from "./types";
import { initAuth } from "./auth";
import { state } from "./state";
import {
  ensureDefinitions,
  ensureHierarchy,
  prefetchCatalogueSoon,
  resetEnsured,
} from "./loaders/ensure";
import {
  loadPatientPlan as loadPatientPlanData,
  loadPatientActuals,
  mergePatientRow,
  readEpisode,
} from "./loaders/patients";
import { loadClientContext } from "./loaders/client-context";
import {
  statusEl,
  clientsView,
  catalogueSearch,
} from "./dom";
import { scheduleAutosave } from "./clients/draft";
import { renderCatalogueForSearch } from "./catalogue/tree";
import { debounce } from "./utils/debounce";
import { bindNavTabs } from "./navigation";
import { bindAdminShell } from "./admin/shell";
import { bindChangePassword } from "./account";
import { initRouter, applyRoute, currentRoute } from "./router";

// State lives in state.ts — imported above as the single `state` object.
// (OMON-035 draft-state declarations that previously lived inline here
// have moved to state.patientDraft / state.currentDraftId /
// state.autosaveDebounceTimer per OMON-060 Phase 5.)

// ======================================================================
// Constants
// ======================================================================

export const LEVEL_NAMES = ["Fully supported", "Partially supported", "With prompting", "Mostly independent", "Independent"];
export const SCORE_LABELS = ["0 -- Not set", "1 -- Fully supported", "2 -- Partially supported", "3 -- With prompting", "4 -- Mostly independent", "5 -- Independent"];

// State lives in state.ts — imported as the single `state` object.
// See the "State" section of state.ts for the full inventory.

// OMON-035: register autosave listener once at module init. The handler
// gates by state.builderMode, so it only fires when a builder session is in
// flight. Listens for input AND change AND click — the picker mode mutates
// state.builderGoals via row clicks, which would otherwise never trigger
// autosave. Same function reference re-passed to addEventListener is
// automatically deduped by the browser.
clientsView.addEventListener("input", scheduleAutosave);
clientsView.addEventListener("change", scheduleAutosave);
clientsView.addEventListener("click", scheduleAutosave);

// OMON-194: small version + build badge, bottom-right with the debug status.
// Constants baked by build.sh via esbuild --define (VERSION file + git short SHA).
declare const __APP_VERSION__: string;
declare const __BUILD_DATE__: string;
declare const __BUILD_ID__: string;
const versionBadge = document.createElement("div");
versionBadge.id = "appVersionBadge";
versionBadge.textContent = `v${__APP_VERSION__} · ${__BUILD_DATE__} · ${__BUILD_ID__}`;
versionBadge.title = "App version · build";
versionBadge.style.cssText =
  "position:fixed;bottom:6px;right:10px;font-size:11px;color:#9ca3af;" +
  "font-family:ui-monospace,SFMono-Regular,Menlo,monospace;pointer-events:none;" +
  "user-select:none;z-index:9999;";
document.body.appendChild(versionBadge);

initAuth(loadData, stopListeners);

bindNavTabs();
// OMON-202: register the hashchange listener so browser back/forward (and
// click-driven nav, which now sets the hash) flow through the router.
initRouter();
// OMON-137: wire the admin hamburger + dropdown. Hamburger itself
// stays hidden until claims load (auth.ts → applyClaimsToShell).
bindAdminShell();
// OMON-195 Part A: wire the left-rail "Password" button (self-service
// change-password). Available to every signed-in user; not claims-gated.
bindChangePassword();

// ======================================================================
// Search filtering
// ======================================================================

// 2026-09-24 perf: each keystroke used to rebuild the whole tree (~330 goals,
// plus SortableJS drop zones in Manage Hierarchy). Debounced to one rebuild
// per pause in typing, and skipped when the effective query is unchanged.
// Enter flushes immediately. Match semantics are unchanged (tree.ts).
const searchCatalogueSoon = debounce(renderCatalogueForSearch, 150);
catalogueSearch.addEventListener("input", () => searchCatalogueSoon());
catalogueSearch.addEventListener("keydown", (e) => {
  if (e.key === "Enter") searchCatalogueSoon.flush();
});


// ======================================================================
// Data loading
// ======================================================================

async function loadData() {
  statusEl.textContent = "Loading...";
  try {
    // OMON-281: the router runs IMMEDIATELY once auth resolves. Boot no longer
    // fetches the catalogue or the patient list on every route's behalf —
    // each route awaits exactly what it needs (loaders/ensure.ts).
    //
    // What used to happen: `await Promise.all([catalogue, activities,
    // hierarchy, patients])` ran first, so opening Connect paid for ~690
    // catalogue documents (~828 KB) that Connect never reads. Measured on an
    // iPhone (2026-07-31), that put `coordinator_sensors` — 4.05s, the fastest
    // request in the whole trace — behind ~2.5 minutes of queueing.
    //
    // OMON-202's guarantee (a deep link like #/clients/<eocId>/tasks resolves
    // after refresh) is preserved by the router awaiting `ensurePatients()`
    // on the routes that resolve an eocId, rather than by boot pre-loading
    // for everyone.
    await applyRoute(currentRoute());
    statusEl.textContent = "Ready";
  } catch (err) {
    console.error("Failed to load data:", err);
    statusEl.textContent = "Error loading data -- check console";
  }
}

/**
 * Thin wrapper over the patient-plan + actuals loaders that threads
 * main.ts state in and assigns the returned data back into state.
 * Phase 5 will replace this with state.ts-backed accessors.
 */
export async function loadPatientPlan(
  patient: PatientInfo,
  // OMON-329: pass-through so a restore can load a NAMED superseded version
  // through the same choke point every other caller uses. Omitted = the
  // current active plan, exactly as before.
  opts: { versionDocId?: string } = {},
) {
  // OMON-281: the plan resolves goals against the catalogue + goal hierarchy,
  // so this is where that dependency is declared and awaited — the single
  // choke point for every caller (router deep link, client-list click,
  // returning from the builder).
  //
  // 2026-09-17: awaiting the WHOLE catalogue here made the first plan of a
  // session take over a minute on a phone (Ed). A plan needs the small goal
  // hierarchy plus the handful of definitions it names, so only those are
  // awaited; the loader fetches each batch just before it looks them up.
  //
  // OMON-351: the hierarchy is no longer awaited up front — the plan loader
  // awaits it just before its first lookup, so it overlaps the reads below.
  const hierarchyReady = ensureHierarchy();
  hierarchyReady.catch(() => {}); // the plan loader's await reports a failure
  const ctx = {
    catalogueData: state.catalogueData,
    goalCategories: state.goalCategories,
    goalSubcategories: state.goalSubcategories,
    ensureDefinitions,
    hierarchyReady,
  };
  // OMON-351: ONE EpisodeOfCare read, shared by the plan and context loaders
  // (each used to read it for itself).
  const episode = readEpisode(patient.docId);
  episode.catch(() => {}); // each loader's own await handles a failure
  // The planStartDate the actuals are indexed against is whatever state held
  // when this load began — exactly what the old sequential call passed, since
  // nothing between here and there assigned it (detail.ts does, at render).
  const planStartDate = state.planStartDate;
  // OMON-149: load client-context (age, address, carers) in parallel
  // with the plan data. Both reads complete before render so the header
  // and body land together — no second-render flash for the new lines.
  // OMON-351: the actuals (+ the definitions the narrative labels with) join
  // the same wait instead of starting after it; they need only the EoC id.
  const [planResult, clientContext, loaded] = await Promise.all([
    loadPatientPlanData(patient, ctx, { ...opts, episode }),
    loadClientContext(patient, { episode }),
    loadPatientActuals(patient, planStartDate).then(async (actuals) => {
      // The narrative labels each recording from its definition (title, and
      // the recording kind that decides whether a value chip shows). Without
      // these it would render raw codes, and nothing re-renders it when the
      // full catalogue later arrives. (ensureDefinitions waits for ids the
      // plan loader is fetching concurrently rather than skipping them.)
      await ensureDefinitions(actuals.narrative.map((e) => e.code));
      return actuals;
    }),
  ]);
  const { carePlans, goals, monitoredEvents, draft, row } = planResult;
  state.patientCarePlans = carePlans;
  state.patientGoals = goals;
  state.patientMonitoredEvents = monitoredEvents;
  state.patientDraft = draft;
  state.clientContext = clientContext;
  // OMON-351: keep this client's list row current from the reads just made
  // (plan pills after a publish / discard / restore) — see ensure.ts.
  mergePatientRow(patient, state.patients, row);
  state.patientActuals = loaded.actuals;
  state.patientNarrative = loaded.narrative;
  // The builder and inventory need everything; start it once this plan has
  // rendered, so it never competes with the reads above.
  prefetchCatalogueSoon();
}









// ======================================================================

// ======================================================================
// Cleanup
// ======================================================================

function stopListeners() {
  state.listeners.forEach((unsub) => unsub());
  state.listeners.length = 0;
  // OMON-281: drop the lazy-load memos, or the next sign-in would read the
  // previous user's cached catalogue and patient list.
  resetEnsured();
  state.catalogueData.clear();
  state.activityData.clear();
  state.goalCategories.clear();
  state.goalSubcategories.clear();
  for (const c of state.activeCharts) c.destroy();
  state.activeCharts = [];
  state.patients = [];
  state.patientCarePlans = [];
  state.patientGoals = [];
  state.patientMonitoredEvents = [];
  state.patientActuals = {};
  state.clientContext = null;
  state.selectedPatient = null;
  state.builderGoals = [];
  state.builderMode = "view";
  state.catalogueSelectedGoal = null;
  state.catalogueEditMode = false;
  state.editingDetail = null;
}

